'use client';

import { type Ref, forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useChildrenOfType } from '../../hooks/use-children-of-type';
import { FormFieldContext } from '../form-field/context/form-field.context';
import { Input } from '../input';
import { inputRecipe } from '../input/styles/input.styles';
import { selectRecipe } from './styles/select.styles';
import type { SelectPropsInterface } from './types/select.types';

/**
 * @since 2.1.0
 */
export const Select = forwardRef<HTMLSelectElement, SelectPropsInterface>(
  (props: SelectPropsInterface, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);
    const {
      children,
      className,
      css: cssProp,
      fluid = true,
      disabled = formFieldContext.disabled || false,
      error = formFieldContext.error || false,
      readOnly = formFieldContext.readOnly || false,
      required = formFieldContext.required || false,
      size = 'md',
      value,
      defaultValue,
      onChange,
      ...rest
    } = props;

    const styles = css(
      inputRecipe.raw({ size, disabled, error, fluid, readOnly }),
      selectRecipe.raw({ disabled, fluid, readOnly }),
      cssProp,
    );

    const options = useChildrenOfType(children, 'option');

    const selectedOption = useMemo(() => {
      if (!readOnly) {
        return null;
      }

      const valueToFind = value || defaultValue;

      const selectedOption =
        options.find((option) => option.props.value === valueToFind || option.props.selected) ??
        options[0];

      return selectedOption?.props?.label || selectedOption?.props?.children;
    }, [options, readOnly, value, defaultValue]);

    if (readOnly) {
      return (
        <Input
          ref={forwardedRef as Ref<HTMLInputElement>}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          size={size}
          error={error}
          value={selectedOption}
          onChange={() => {}}
        />
      );
    }
    return (
      <select
        ref={forwardedRef}
        aria-describedby={formFieldContext.descriptionId}
        aria-invalid={error ?? undefined}
        className={cx('c11n', 'c11n_select', styles, className)}
        data-c11n-component="Select"
        data-c11n-version={__C11N_VERSION__}
        disabled={disabled}
        id={formFieldContext.controlId}
        onChange={onChange}
        required={required}
        value={value}
        defaultValue={defaultValue}
        {...rest}
      >
        {children}
      </select>
    );
  },
);
Select.displayName = 'Select';
